import { useCallback, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragStartEvent, DragEndEvent } from "@dnd-kit/core";
import type { BoardLead, LeadStage, PipelineStage } from "@school-sales-ai/shared";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import {
  useMoveCard,
  groupByCycleAndStage,
  useStageOverflow,
  useSwimlaneMode,
  PER_STAGE_LIMIT,
  MAX_STAGE_LIMIT,
} from "@/hooks/usePipeline";
import type { GroupedLeads } from "@/hooks/usePipeline";
import { useCatalogContext } from "@/hooks/useCatalog";
import { resolveDragAction, resolveDropStage, resolveStageChange } from "@/lib/board-drag";
import type { DragAction } from "@/lib/board-drag";
import { timeAgo } from "@/lib/time-ago";
import { stageClasses } from "@/lib/stage-config";
import { Avatar } from "@/components/Avatar";
import { ChannelIcon } from "@/components/ChannelIcon";
import { KanbanCard } from "@/components/KanbanCard";
import { KanbanColumn } from "@/components/KanbanColumn";
import { StageReasonModal } from "@/components/StageReasonModal";
import { LabelChip } from "@/components/LabelChip";
import { useCrmStore } from "@/stores/crm.store";
import { BoardMoveContext } from "./board-move-context";
import type { BoardMove } from "./board-move-context";

type PendingMove = {
  leadId: string;
  stage: PipelineStage;
};

type MobileCardProps = {
  lead: BoardLead;
  activeStage: LeadStage;
  orderedKeys: string[];
  onMove: (leadId: string, toStage: string) => void;
};

function MobilePipelineCard({ lead, activeStage, orderedKeys, onMove }: MobileCardProps): React.JSX.Element {
  const navigate = useNavigate();
  const stageIdx = orderedKeys.indexOf(activeStage);
  const prevStage = stageIdx > 0 ? orderedKeys[stageIdx - 1] : null;
  const nextStage = stageIdx >= 0 && stageIdx < orderedKeys.length - 1 ? orderedKeys[stageIdx + 1] : null;

  return (
    <button
      type="button"
      onClick={() => navigate(`/leads/${lead.id}`, { state: { from: "/pipeline" } })}
      className="w-full text-left bg-white border border-ink-100 rounded-[14px] p-[14px] shadow-sm active:bg-ink-050 transition-colors"
    >
      {/* Top: Avatar + name/program + ScoreBadge */}
      <div className="flex items-center gap-2.5 mb-2.5">
        <Avatar name={lead.name ?? lead.sessionRef} size={36} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-ink-900 truncate">
            {lead.name ?? <span className="italic text-ink-400">Sin nombre</span>}
          </div>
        </div>
        <ScoreBadge score={lead.score} size="sm" />
      </div>

      {/* Labels (display-only on mobile; add/manage from the lead detail page) */}
      {lead.labels.length > 0 && (
        <div className="mb-2.5 flex flex-wrap gap-1">
          {lead.labels.map((label) => (
            <LabelChip key={label.id} label={label} />
          ))}
        </div>
      )}

      {/* Bottom: ChannelIcon + time + quick-move buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <ChannelIcon channel={lead.channelOrigin} />
          <span className="text-[10px] text-ink-400">· {timeAgo(lead.lastInteractionAt)}</span>
        </div>
        <div
          className="flex gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {prevStage && (
            <button
              type="button"
              onClick={() => onMove(lead.id, prevStage)}
              className="px-2 py-1 bg-ink-100 rounded-[6px] text-[11px] text-ink-700 cursor-pointer"
            >
              ← Atrás
            </button>
          )}
          {nextStage && (
            <button
              type="button"
              onClick={() => onMove(lead.id, nextStage)}
              className="px-2 py-1 bg-lime-pale rounded-[6px] text-[11px] text-moss font-semibold cursor-pointer"
            >
              Avanzar →
            </button>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * One paginated board column.
 *
 * Its own component because the overflow query is a hook — it cannot run in a
 * loop inside BoardView. While the user has not scrolled past the first page,
 * `useStageOverflow` is disabled and the column renders the slice that came with
 * the main board request.
 */
function BoardColumn({
  stage,
  pageLeads,
  count,
  activeId,
}: {
  stage: PipelineStage;
  pageLeads: BoardLead[];
  count: number;
  activeId: string | null;
}): React.JSX.Element {
  const limit = useCrmStore((s) => s.stageLimits[stage.key]);
  const growStage = useCrmStore((s) => s.growStage);
  const setStages = useCrmStore((s) => s.setStages);
  const setViewMode = useCrmStore((s) => s.setViewMode);
  const overflow = useStageOverflow(stage.key, limit);
  const loaded = overflow ?? pageLeads;
  // Stop offering "Ver más" at the server's ceiling — the Tabla link below it is
  // the way to the rest.
  const canLoadMore = (limit ?? PER_STAGE_LIMIT) < MAX_STAGE_LIMIT;

  return (
    <KanbanColumn
      stage={stage}
      leads={loaded}
      count={count}
      activeId={activeId}
      onLoadMore={
        canLoadMore ? () => growStage(stage.key, PER_STAGE_LIMIT) : undefined
      }
      loadMoreCount={Math.min(PER_STAGE_LIMIT, count - loaded.length)}
      onSeeAll={() => {
        setStages([stage.key]);
        setViewMode("table");
      }}
    />
  );
}

type Props = {
  stages: PipelineStage[];
  grouped: GroupedLeads;
  /** True per-stage totals from the server — the loaded page is only a slice. */
  counts: Record<string, number>;
  isLoading: boolean;
};

/**
 * The Kanban board half of Pipeline de Prospectos.
 *
 * `stages`/`grouped` always span every pipeline column — the stage filter narrows
 * what is *rendered* (`visibleStages`), never what is fetched, so the counts the
 * page hands to ManageColumnsModal stay honest. See `useBoardFilters`.
 */
export function BoardView({ stages, grouped, counts, isLoading }: Props): React.JSX.Element {
  const moveCard = useMoveCard();
  const stageFilter = useCrmStore((s) => s.stages);
  const cycleFilter = useCrmStore((s) => s.cycleIds);
  const collapsedLanes = useCrmStore((s) => s.collapsedLanes);
  const toggleLane = useCrmStore((s) => s.toggleLane);

  const orderedKeys = stages.map((s) => s.key);
  const visibleStages =
    stageFilter.length > 0 ? stages.filter((s) => stageFilter.includes(s.key)) : stages;

  // Cycle swimlanes: only when the product filter points at a cycle-based
  // product. Lanes = all active cycles + a "Sin ciclo" lane (desktop only).
  const { data: catalog } = useCatalogContext();
  const cycles = catalog?.cycles ?? [];
  const showLanes = useSwimlaneMode();
  const allLanes = showLanes
    ? [
        ...cycles.map((c) => ({ key: c.id, label: c.label })),
        { key: "none", label: "Sin ciclo" },
      ]
    : [];
  // A cycle filter already excluded every other lane's leads server-side —
  // rendering the empty lanes anyway would just be a wall of "Sin prospectos".
  const lanes =
    cycleFilter.length > 0 ? allLanes.filter((l) => cycleFilter.includes(l.key)) : allLanes;
  const allLeads = orderedKeys.flatMap((k) => grouped[k] ?? []);
  const laneGrouped = showLanes
    ? groupByCycleAndStage(allLeads, orderedKeys, cycles.map((c) => c.id))
    : null;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [activeStageKey, setActiveStageKey] = useState<string | null>(null);
  // Fall back to the first visible column until the user picks one (or after a
  // column is deleted or filtered away). `null` only while stages are loading.
  const visibleKeys = visibleStages.map((s) => s.key);
  const activeStage =
    activeStageKey && visibleKeys.includes(activeStageKey)
      ? activeStageKey
      : (visibleKeys[0] ?? null);

  // The mobile board shows one stage at a time, so it needs that stage's page —
  // the same cache entry the desktop column uses, so this costs no extra request.
  const mobileLimit = useCrmStore((st) =>
    activeStage ? st.stageLimits[activeStage] : undefined,
  );
  const growStage = useCrmStore((st) => st.growStage);
  const mobileOverflow = useStageOverflow(activeStage ?? "", mobileLimit);
  const mobileLeads =
    mobileOverflow ?? (activeStage ? (grouped[activeStage] ?? []) : []);
  const mobileCount = activeStage
    ? (counts[activeStage] ?? mobileLeads.length)
    : 0;
  const mobileHasMore = mobileLeads.length < mobileCount;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  // The one place a move happens, whoever asked: a drag, the phone's arrows or a
  // card's «Mover a…». A `requiresReason` column parks the move on the modal.
  function applyMove(leadId: string, action: DragAction): void {
    if (action.type === "no-op") return;
    if (action.type === "modal") {
      const stage = stages.find((s) => s.key === action.toStage);
      if (stage) setPendingMove({ leadId, stage });
      return;
    }
    moveCard.mutate({ leadId, toStage: action.toStage });
  }

  // The menu knows the card's own stage, so it needs no lookup in `grouped` (which
  // does not hold the cards an expanded column fetched on its own).
  function moveFromMenu(leadId: string, fromStage: string, toStage: string): void {
    applyMove(leadId, resolveStageChange(fromStage, toStage, stages));
  }
  // Stable identity, latest closure: the context value below must not change on
  // every render, yet the move must see this render's columns.
  const moveFromMenuRef = useRef(moveFromMenu);
  moveFromMenuRef.current = moveFromMenu;
  const requestMove = useCallback(
    (leadId: string, fromStage: string, toStage: string) =>
      moveFromMenuRef.current(leadId, fromStage, toStage),
    [],
  );
  // Memoized on `stages` alone, so a render of the board does not re-render the
  // menu of every card on it.
  const boardMove = useMemo<BoardMove>(() => ({ stages, requestMove }), [stages, requestMove]);

  function handleDragStart({ active }: DragStartEvent): void {
    setActiveId(String(active.id));
  }

  function handleDragEnd({ active, over }: DragEndEvent): void {
    setActiveId(null);

    if (!over) return;

    const leadId = String(active.id);
    const toStageId = resolveDropStage(String(over.id), leadId, allLeads);
    if (toStageId === null) return;

    applyMove(leadId, resolveDragAction(leadId, toStageId, grouped, stages));
  }

  function handleModalConfirm(): void {
    if (!pendingMove) return;
    moveCard.mutate({
      leadId: pendingMove.leadId,
      toStage: pendingMove.stage.key,
    });
    setPendingMove(null);
  }

  const activeLead = activeId
    ? (allLeads.find((l) => l.id === activeId) ?? null)
    : null;

  return (
    <>
      {/* ── Mobile ────────────────────────────────── */}
      <div className="md:hidden flex flex-col flex-1 min-h-0">
        {/* Stage tab bar */}
        <div className="bg-white border-b border-ink-100 overflow-x-auto flex-shrink-0 [&::-webkit-scrollbar]:hidden">
          <div className="flex px-2 gap-0.5 w-max">
            {visibleStages.map((stage) => {
              // Server total — the loaded page is only the top slice.
              const count = counts[stage.key] ?? (grouped[stage.key] ?? []).length;
              const isActive = stage.key === activeStage;
              return (
                <button
                  key={stage.key}
                  type="button"
                  onClick={() => setActiveStageKey(stage.key)}
                  className={`flex flex-col items-center gap-[3px] px-3 py-2.5 flex-shrink-0 border-b-2 transition-all cursor-pointer ${
                    isActive ? "border-moss" : "border-transparent"
                  }`}
                >
                  <span
                    className={`text-[11px] whitespace-nowrap ${
                      isActive
                        ? "font-semibold text-moss"
                        : "font-normal text-ink-600"
                    }`}
                  >
                    {stage.label}
                  </span>
                  <span
                    className={`text-[10px] font-semibold px-[7px] py-[1px] rounded-full ${
                      isActive
                        ? "bg-lime text-moss"
                        : stageClasses(stage.key, stages)
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Card list */}
        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ink-300">
          {isLoading &&
            [1, 2, 3].map((i) => (
              <div key={i} className="h-[90px] rounded-[14px] bg-ink-100 animate-pulse" />
            ))}
          {!isLoading && activeStage && mobileLeads.length === 0 && (
            <div className="flex flex-col items-center py-12 gap-2">
              <span className="text-3xl opacity-25">📭</span>
              <span className="font-display font-bold text-[18px] text-ink-400">
                Sin leads en esta etapa
              </span>
            </div>
          )}
          {!isLoading &&
            activeStage &&
            mobileLeads.map((lead) => (
              <MobilePipelineCard
                key={lead.id}
                lead={lead}
                activeStage={activeStage}
                orderedKeys={orderedKeys}
                onMove={(leadId, toStage) =>
                  applyMove(leadId, resolveDragAction(leadId, toStage, grouped, stages))
                }
              />
            ))}

          {!isLoading && activeStage && mobileHasMore && (
            <div className="flex flex-col items-center gap-1.5 py-3">
              <span className="text-[11px] text-ink-400">
                {mobileLeads.length} de {mobileCount}
              </span>
              {(mobileLimit ?? PER_STAGE_LIMIT) < MAX_STAGE_LIMIT && (
                <button
                  type="button"
                  onClick={() => growStage(activeStage, PER_STAGE_LIMIT)}
                  className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-ink-700 cursor-pointer"
                >
                  Ver {Math.min(PER_STAGE_LIMIT, mobileCount - mobileLeads.length)} más
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Desktop ───────────────────────────────── */}
      <div className="hidden md:flex flex-col flex-1 min-h-0">
        {isLoading ? (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {[0, 1, 2, 3, 4].map((col) => (
              <div key={col} className="flex w-64 flex-shrink-0 flex-col gap-2">
                <div className="h-4 w-24 animate-pulse rounded bg-ink-100" />
                <div className="min-h-[120px] rounded-xl bg-ink-050 p-2">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="mb-2 h-20 animate-pulse rounded-lg bg-ink-100"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <BoardMoveContext.Provider value={boardMove}>
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {showLanes && laneGrouped ? (
              <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto pb-4">
                {lanes.map((lane) => {
                  const laneTotal = orderedKeys.reduce(
                    (n, k) => n + (laneGrouped[lane.key]?.[k]?.length ?? 0),
                    0,
                  );
                  // A collapsed lane renders its header and nothing else, so its
                  // cards never mount. "Sin ciclo" starts collapsed: on the
                  // pilot's biggest product it is 90 of 101 leads.
                  const collapsed = collapsedLanes[lane.key] ?? false;
                  return (
                    <div key={lane.key} className="flex flex-col gap-2">
                      {/* Lane header band — sticky to stay visible on horizontal scroll */}
                      <button
                        type="button"
                        onClick={() => toggleLane(lane.key)}
                        aria-expanded={!collapsed}
                        className="sticky left-0 flex items-center gap-2 self-start rounded-md border border-ink-100 bg-white px-3 py-1 shadow-sm cursor-pointer hover:bg-ink-050"
                      >
                        {collapsed ? (
                          <ChevronRight size={14} className="text-ink-400" />
                        ) : (
                          <ChevronDown size={14} className="text-ink-400" />
                        )}
                        <span className="text-sm font-semibold text-ink-800">
                          {lane.label}
                        </span>
                        <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs font-semibold text-ink-600">
                          {laneTotal}
                        </span>
                      </button>
                      {/* Columns row */}
                      {!collapsed && (
                        <div className="flex w-max gap-3">
                          {visibleStages.map((stage) => (
                            <KanbanColumn
                              key={stage.key}
                              stage={stage}
                              leads={laneGrouped[lane.key]?.[stage.key] ?? []}
                              // Lane mode is unpaginated, so the loaded cell IS
                              // the whole cell.
                              count={(laneGrouped[lane.key]?.[stage.key] ?? []).length}
                              activeId={activeId}
                              laneKey={lane.key}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-4">
                {visibleStages.map((stage) => (
                  <BoardColumn
                    key={stage.key}
                    stage={stage}
                    pageLeads={grouped[stage.key] ?? []}
                    count={counts[stage.key] ?? (grouped[stage.key] ?? []).length}
                    activeId={activeId}
                  />
                ))}
              </div>
            )}

            <DragOverlay>
              {activeLead ? (
                <div className="rotate-1 opacity-90 shadow-lg">
                  <KanbanCard lead={activeLead} isDragging={false} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
          </BoardMoveContext.Provider>
        )}
      </div>

      {pendingMove && (
        <StageReasonModal
          stage={pendingMove.stage}
          leadId={pendingMove.leadId}
          open
          onConfirm={handleModalConfirm}
          onCancel={() => setPendingMove(null)}
        />
      )}
    </>
  );
}
